import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './cn';
import { Tooltip } from './Tooltip';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  /** Show only the icon; the label becomes the accessible name and tooltip. */
  hideLabel?: boolean;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Name the group with `aria-label` or `aria-labelledby`. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  size?: 'sm' | 'md';
  /** Stretch segments to fill the container (mobile view switchers). */
  fullWidth?: boolean;
  className?: string;
}

/**
 * Single choice among two to five visible options, such as "Table | Map".
 * Radio-group semantics: Tab enters the group at the selected option and
 * arrow keys move and select.
 */
export const SegmentedControl = <T extends string>({
  value,
  onValueChange,
  options,
  size = 'md',
  fullWidth = false,
  className,
  ...labelProps
}: SegmentedControlProps<T>) => {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => !option.disabled);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const tabStop =
    selectedIndex >= 0 && !options[selectedIndex].disabled
      ? selectedIndex
      : (enabled[0]?.index ?? -1);

  const select = (index: number) => {
    refs.current[index]?.focus();
    if (options[index].value !== value) onValueChange(options[index].value);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (enabled.length === 0) return;
    const position = enabled.findIndex((entry) => entry.index === index);
    let next: number | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = enabled[(position + 1) % enabled.length].index;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = enabled[(position - 1 + enabled.length) % enabled.length].index;
        break;
      case 'Home':
        next = enabled[0].index;
        break;
      case 'End':
        next = enabled[enabled.length - 1].index;
        break;
      default:
        return;
    }
    event.preventDefault();
    select(next);
  };

  return (
    <div
      role="radiogroup"
      {...labelProps}
      className={cn(
        'items-center gap-0.5 rounded-lg border border-border bg-surface-sunken p-0.5',
        fullWidth ? 'flex w-full' : 'inline-flex',
        className
      )}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        const segment = (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.hideLabel ? option.label : undefined}
            disabled={option.disabled}
            tabIndex={index === tabStop ? 0 : -1}
            onClick={() => select(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md border font-medium transition-colors duration-150 [&_svg]:shrink-0',
              size === 'sm'
                ? 'h-[26px] px-2.5 text-xs [&_svg]:size-3.5'
                : 'h-[30px] px-3 text-sm [&_svg]:size-4',
              option.hideLabel &&
                (size === 'sm' ? 'w-[26px] px-0' : 'w-[30px] px-0'),
              fullWidth && 'flex-1',
              checked
                ? 'border-border bg-surface-raised text-fg shadow-sm'
                : 'border-transparent text-fg-muted enabled:hover:text-fg',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {option.icon != null && (
              <span aria-hidden="true" className="flex">
                {option.icon}
              </span>
            )}
            {!option.hideLabel && option.label}
          </button>
        );
        return option.hideLabel ? (
          <Tooltip key={option.value} content={option.label}>
            {segment}
          </Tooltip>
        ) : (
          segment
        );
      })}
    </div>
  );
};
